"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { ShieldCheck, ThermometerSun, Award, Calendar, CirclePlay } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const features = [{
  icon: ShieldCheck,
  title: "מטפל סיעודי בבית",
  description: "ליווי אישי ומקצועי בסביבה המוכרת והבטוחה של המטופל, תוך שמירה על כבודו."
}, {
  icon: ThermometerSun,
  title: "השגחה בבית חולים",
  description: "צוות זמין למתן מענה מיידי והשגחה צמודה בבתי החולים בירושלים והסביבה."
}, {
  icon: Award,
  title: "ניסיון ומקצועיות",
  description: "מטפלים מוסמכים עם המלצות מוכחות וניסיון רב בטיפול סיעודי מורכב."
}];
export default function PatioDoorGlassRepairMontreal() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252482" id="מטפל-סיעודי-בירושלים-פתרון-מיידי" className="bg-background text-foreground py-20 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-accent/20 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-[120px]" />
      </div>

      <div className="page-container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {}
          <div ref={textRef} className={`flex flex-col items-start transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20 mb-6 font-default">
              שירות מיידי בירושלים והסביבה
            </Badge>
            
            <h1 className="font-default text-4xl md:text-5xl lg:text-[68px] leading-tight font-bold mb-6 text-foreground">
              מטפל סיעודי בירושלים <span className="text-primary">תוך 5 שעות</span>
            </h1>
            
            <p className="font-default text-lg md:text-[20px] text-muted-foreground mb-10 max-w-xl leading-relaxed">
              זקוקים לעזרה דחופה? אנו מספקים פתרונות סיעוד מקצועיים בירושלים במהירות שיא. בין אם מדובר ב <Link to="/CaregiverServicePagesPage" className="text-primary underline">מטפל סיעודי בבית</Link> או ב <Link to="/CaregiverServicePagesPage2" className="text-primary underline">השגחה בבית חולים</Link>, הצוות שלנו ערוך להגיע אליכם. אנו פועלים גם בערים נוספות, כולל שירות <Link to="/NurseInTelAvivWithinHoursPage" className="text-primary underline">מטפל סיעודי בתל אביב</Link>.
            </p>

            <ul className="space-y-6 mb-10 w-full max-w-xl">
              {features.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-default text-xl font-semibold text-foreground mb-1">{feature.title}</h3>
                    <p className="font-default text-muted-foreground text-sm md:text-base">{feature.description}</p>
                  </div>
                </li>)}
            </ul>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-default text-base h-14 px-8 rounded-lg shadow-lg">
                <Link to="/ContactPage">
                  <SafeHydrate><Calendar className="w-5 h-5 ml-2" /></SafeHydrate>
                  הזמנת מטפל עכשיו
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-transparent border-border text-foreground hover:bg-accent font-default text-base h-14 px-8 rounded-lg">
                <Link to="/ProfessionalBlogPage">
                  <CirclePlay className="w-5 h-5 ml-2" />
                  איך זה עובד?
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className={`relative transition-all duration-1000 delay-200 ease-out ${isImageVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="relative rounded-lg overflow-hidden shadow-xl border border-border">
              <AspectRatio ratio={4 / 5} className="bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/jerusalem_caregiver_hero.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/jerusalem_caregiver_hero.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1626204/img/jerusalem_caregiver_hero.png-wvc-sizes" alt="מטפל סיעודי מקצועי בירושלים מעניק עזרה לקשיש" className="object-cover w-full h-full" loading="eager" />
                {}
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
              </AspectRatio>
              
              {}
              <div className="absolute bottom-6 left-6 right-6 bg-card/80 backdrop-blur-md border border-border rounded-lg p-4 flex items-center gap-4">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((_, i) => <img key={i} data-index={i} src={`/img/satisfied_family_${i + 1}.png`} data-wvc-srcset={`/img/satisfied_family_${i + 1}.png-wvc-srcset`} data-wvc-sizes={`/img/satisfied_family_${i + 1}.png-wvc-sizes`} alt="משפחה מרוצה" className="w-10 h-10 rounded-lg border-2 border-background object-cover" />)}
                </div>
                <div className="font-default">
                  <div className="flex items-center gap-1 text-yellow-500 mb-0.5">
                    {[1, 2, 3, 4, 5].map((_, i) => <svg key={i} data-index={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>)}
                  </div>
                  <p className="text-foreground text-sm font-medium">מאות משפחות בירושלים כבר בחרו בנו</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}